import { analyzeContactSharing, checkViolation } from './messageValidator';
import { notifyContactSharingViolation, notifyModerationEvent } from './api';

export const ERROR_CODE_CONTACT_INFO_DETECTED = 'CONTACT_INFO_DETECTED';
export const DIRECT_CONTACT_LISTING_TYPE = 'direct-contact-service';
export const SHADOW_BAN_THRESHOLD = 50;
export const SUSPICIOUS_EVENT = 'contact_sharing_suspicious';

/**
 * Maps an internal block reason to a safe, broad public translation id.
 * Never exposes regexes, matched text, or bypass-sensitive detail.
 * Unknown/empty/multiple reasons fall back to the generic contact-info id.
 */
export const contactReasonMessageId = (reason, fallbackId = 'Common.contactInfoDetected') => {
  switch (reason) {
    case 'url':
      return 'Common.contactInfoDetected.url';
    case 'email':
      return 'Common.contactInfoDetected.email';
    case 'phone':
      return 'Common.contactInfoDetected.phone';
    case 'stop_words':
      return 'Common.contactInfoDetected.social';
    default:
      return fallbackId;
  }
};

export const allowsContactInfoForListing = listing => {
  const listingType = listing?.attributes?.publicData?.listingType;
  return typeof listingType === 'string' && listingType === DIRECT_CONTACT_LISTING_TYPE;
};

export const detectContactViolation = (text, siteDomain) => {
  const validation = checkViolation(text, siteDomain);
  return validation?.isViolated ? validation : null;
};

/**
 * Report-only moderation signal for ambiguous contact sharing.
 *
 * Observability only: it never rejects, never delays, never touches
 * violationCount / shadow-ban / fake-success, and always returns undefined.
 * The whole side effect is wrapped in try/catch so that a throwing analyzer,
 * transport or serializer cannot escape into the caller's flow.
 *
 * `text` accepts a single string or an array of independent messages. Array
 * inputs are analyzed separately and never concatenated: joining them could
 * manufacture a candidate that exists in neither message on its own.
 */
export const reportSuspiciousContactSharing = ({ text, siteDomain, context, user }) => {
  try {
    const inputs = (Array.isArray(text) ? text : [text]).filter(
      value => typeof value === 'string' && value.trim()
    );
    const analyzed = inputs.map(value => ({
      value,
      decision: analyzeContactSharing(value, siteDomain),
    }));

    // Pass 1: a block anywhere in the input suppresses suspicious reporting.
    if (analyzed.some(({ decision }) => decision?.decision === 'block')) {
      return undefined;
    }

    // Pass 2: the first suspicious input in original order wins.
    const suspicious = analyzed.find(({ decision }) => decision?.decision === 'suspicious');
    if (!suspicious) {
      return undefined;
    }

    const sent = notifyModerationEvent({
      event: SUSPICIOUS_EVENT,
      alert_title: 'Contact sharing review',
      status_text: `Ambiguous contact info detected in ${context || 'unknown-context'}.`,
      violation_count: null,
      name: user?.attributes?.profile?.displayName || 'Unknown user',
      userId: user?.id?.uuid || 'unknown',
      message: suspicious.value,
      context: context || '',
      reason: suspicious.decision.reason || '',
    });
    if (sent && typeof sent.then === 'function') {
      sent.catch(() => {});
    }
  } catch (e) {
    // Intentionally swallowed: reporting must never affect the user's action.
  }
  return undefined;
};

/**
 * Recursively collects all string values from any data structure.
 * If object keys are listed in ignoredKeys, those branches are skipped.
 */
export const collectTextDeep = (value, ignoredKeys = []) => {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string') return [value];
  if (typeof value === 'number' || typeof value === 'boolean') return [String(value)];
  if (Array.isArray(value)) return value.flatMap(item => collectTextDeep(item, ignoredKeys));
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, nestedValue]) =>
      ignoredKeys.includes(key) ? [] : collectTextDeep(nestedValue, ignoredKeys)
    );
  }
  return [];
};

const collectIgnoredTextDeep = (value, ignoredKeys = []) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.flatMap(item => collectIgnoredTextDeep(item, ignoredKeys));
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, nestedValue]) => {
      if (ignoredKeys.includes(key)) {
        return collectTextDeep(nestedValue);
      }
      return collectIgnoredTextDeep(nestedValue, ignoredKeys);
    });
  }
  return [];
};

/**
 * Unified contact-info guard for use inside createAsyncThunk payload creators.
 * Returns the rejectWithValue result if a violation is found, otherwise null.
 *
 * Usage:
 *   const guard = executeContactGuard({
 *     data,
 *     siteDomain,
 *     context,
 *     user,
 *     ignoredKeys,
 *     rejectWithValue,
 *   });
 *   if (guard) return guard;
 */
export const executeContactGuard = ({
  data,
  siteDomain,
  context,
  user,
  ignoredKeys = [],
  rejectWithValue,
}) => {
  const fullText = collectTextDeep(data, ignoredKeys).join(' ');
  if (!fullText.trim()) {
    return null;
  }
  const violation = detectContactViolation(fullText, siteDomain);
  const ignoredText = collectIgnoredTextDeep(data, ignoredKeys).join(' ');
  const ignoredViolation = ignoredText ? detectContactViolation(ignoredText, siteDomain) : null;

  const shouldBlockIgnoredViolation = ignoredViolation && ignoredViolation.reason !== 'phone';
  const effectiveViolation = violation || (shouldBlockIgnoredViolation ? ignoredViolation : null);
  const violationText = violation ? fullText : ignoredText;

  if (effectiveViolation) {
    notifyAdminOfContactAttempt({
      text: violationText,
      user,
      context,
      reason: effectiveViolation.reason,
    });
    return rejectWithValue({
      code: ERROR_CODE_CONTACT_INFO_DETECTED,
      reason: effectiveViolation.reason,
    });
  }

  reportSuspiciousContactSharing({ text: fullText, siteDomain, context, user });
  return null;
};

export const notifyAdminOfContactAttempt = ({ text, user, context, reason }) => {
  const displayName = user?.attributes?.profile?.displayName || 'Unknown user';
  const userId = user?.id?.uuid || 'unknown';
  const payload = {
    alert_title: '⚠️ CONTENT BLOCKED',
    status_text: `Contact info detected in ${context || 'unknown-context'}.`,
    violation_count: null,
    name: displayName,
    userId,
    message: text || '',
    context: context || '',
    reason: reason || '',
  };

  return notifyContactSharingViolation(payload);
};
